import "@material/web/dialog/dialog";

import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { QPilotPaymentMethod } from "../types/payment-method";
import {
  ChangePaymentAndProcessNowEvent,
  ChangePaymentEvent,
  ResumeOrderEvent,
  RescheduleOrderEvent,
} from "../types/events";
import {
  AsyncPipe,
  CurrencyPipe,
  DatePipe,
  DecimalPipe,
  NgClass,
  NgIf,
  NgStyle,
} from "@angular/common";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Overlay } from "@angular/cdk/overlay";
import { BarTagComponent } from "../bar-tag/bar-tag.component";
import {
  OrderStatus,
  QPilotScheduledOrderTotals,
} from "../types/scheduled-order";
import { Observable, map } from "rxjs";
import { NodDatePickerComponent } from "../nod-date-picker/nod-date-picker.component";
import { NodPickerConfig } from "../types/site";
import { provideNativeDateAdapter } from "@angular/material/core";
import { DateFormatPipe } from "../pipes/date-format-pipe";

@Component({
  selector: "lib-scheduled-order-payment-method",
  standalone: true,
  imports: [
    BarTagComponent,
    MatCard,
    MatCardContent,
    MatDialogModule,
    MatIcon,
    NgStyle,
    NgClass,
    AsyncPipe,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    NgIf,
    NodDatePickerComponent,
    DateFormatPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./scheduled-order-payment-method.component.html",
  styleUrl: "./scheduled-order-payment-method.component.scss",
})
export class ScheduledOrderPaymentMethodComponent implements OnChanges, OnInit {
  @Input() orderStatus: OrderStatus;
  @Input() paymentMethod?: QPilotPaymentMethod | undefined;
  @Input() scheduledOrderId: number;
  @Input() enableSelection: boolean = false;
  @Input() currentPaymentMethod: boolean = false;
  @Input() orderVerboseName: string;
  @Input() orderTotals$: Observable<QPilotScheduledOrderTotals>;
  @Input() errorCode?: number;
  @Input() selectedDate?: Date;
  @Input() NODDate?: string;
  @Output() changePayment = new EventEmitter();
  @Output() applyPayment = new EventEmitter<ChangePaymentEvent>();
  @Output() applyPaymentAndProcessNow =
    new EventEmitter<ChangePaymentAndProcessNowEvent>();
  @Output() resumeOrder = new EventEmitter<ResumeOrderEvent>();
  @Output() updateNextOccurrence = new EventEmitter<RescheduleOrderEvent>();

  result$: Observable<{ savings: number; total: number }>;

  @ViewChild("applyDialog") paymentDialog: TemplateRef<unknown>;

  actionName: string;
  actionCallback: (() => void) | undefined;
  dialogRef: MatDialogRef<unknown>;
  resumeEnabled: boolean = false;
  // Date picker properties
  isDateInPast: boolean = false;
  newSelectedDate: Date | null = null;
  defaultDate: Date = new Date();
  nodPickerConfig: NodPickerConfig = {
    supportsProcessingWindow: false,
  };

  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
  ) {}

  ngOnInit(): void {
    this.result$ = this.orderTotals$?.pipe(
      map((totals) => ({
        savings: totals.itemsTotals.subTotal - totals.itemsTotals.total,
        total: totals.total,
      })),
    );

    if (this.NODDate) {
      this.isDateInPast = this.dateInThePast(new Date(this.NODDate));
      this.resumeEnabled = !this.isDateInPast;
      this.defaultDate = new Date(this.NODDate);
    }
  }

  /**
   * Checks if a date is in the past
   * @param date The date to check
   * @returns true if the date is in the past, false otherwise
   */
  dateInThePast(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate < today;
  }

  /**
   * Handles date change from the NOD date picker
   * @param date The new date selected
   */
  onDateChanged(date: Date | null): void {
    this.newSelectedDate = date;
    this.resumeEnabled = !!date;
    // this.isDateInPast = date ? this.dateInThePast(date) : true;
    this.updateNextOccurrence.emit({
      orderId: this.scheduledOrderId,
      nextOccurrenceUtc: date!,
    });
  }

  ngOnChanges() {
    if (this.enableSelection) {
      this.actionName = this.currentPaymentMethod ? "Selected" : "Apply";
      this.actionCallback = !this.currentPaymentMethod
        ? () => this.openDialogWithRef(this.paymentDialog)
        : undefined;
    } else {
      this.actionName = "Change";
      this.actionCallback = () => this.onChangePayment();
    }
  }

  onChangePayment() {
    this.changePayment.emit();
  }

  onApplyPayment() {
    this.applyPayment.emit({
      scheduledOrderId: this.scheduledOrderId,
      paymentMethodId: this.paymentMethod!.id,
    } as ChangePaymentEvent);
    this.dialogRef.close();
  }

  applyAndProcess() {
    this.applyPaymentAndProcessNow.emit({
      scheduledOrderId: this.scheduledOrderId,
      paymentMethodId: this.paymentMethod!.id,
    } as ChangePaymentAndProcessNowEvent);
    this.dialogRef.close();
  }

  resumeAndApplyPayment() {
    this.resumeOrder.emit({
      orderId: this.scheduledOrderId,
    } as ResumeOrderEvent);

    setTimeout(() => {
      this.applyPayment.emit({
        scheduledOrderId: this.scheduledOrderId,
        paymentMethodId: this.paymentMethod!.id,
      } as ChangePaymentEvent);
    }, 5000);

    this.dialogRef.close();
  }

  openDialogWithRef(templateRef: TemplateRef<unknown>) {
    this.dialogRef = this.dialog.open(templateRef, {
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
  }
}
